import React, { useState, useEffect } from "react";
import DiscordIcon from "@/components/icons/DiscordIcon";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { UserProfileDropdown } from "@/components/UserProfileDropdown";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/context/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { ContentType } from "@/types/content";
import { ContentIcon } from "@/components/content/ContentIcon";
import { Room } from "@/types/rooms";
import { useToast } from "@/hooks/use-toast";

import { useTheme } from "@/components/ThemeProvider";
import logoDark from "@/assets/shattara-ai-dark.png";
import logoLight from "@/assets/shattara-ai-light.png";
import { SearchCommand } from "@/components/SearchCommand";
import { FeedbackPopover } from "@/components/FeedbackPopover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CalculatorDialog } from "@/components/CalculatorDialog";
import { QuickGuideDialog } from "@/components/QuickGuideDialog";
import { Plus, Search, History, Sparkles, ThumbsUp, Book, NotebookPen, ChevronRight, ChevronsLeft, MoreVertical, Box, Calculator, ChartNoAxesColumnIncreasing, Share2, Pencil, Trash2, FolderPlus } from "lucide-react";
import { TeamIcon } from "@/components/icons/TeamIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { RoomShareDialog } from "@/components/rooms/RoomShareDialog";
import { RoomDeleteDialog } from "@/components/rooms/RoomDeleteDialog";
import { ContentShareDialog } from "@/components/content/ContentShareDialog";
import { ContentDeleteDialog } from "@/components/content/ContentDeleteDialog";
import { ContentItem } from "@/types/content";
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}
interface NavButtonProps {
  icon: React.ReactNode;
  children: React.ReactNode;
  href?: string;
  isActive?: boolean;
  badge?: boolean;
  onClick?: () => void;
}
interface RecentItem {
  id: string;
  title: string;
  type: ContentType;
  href: string;
}
const RECENTS_LIMIT = 4;
const NavButton = React.forwardRef<HTMLButtonElement, NavButtonProps>(({
  icon,
  children,
  href,
  isActive = false,
  badge = false,
  onClick
}, ref) => {
  const base = "whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 rounded-lg w-full flex justify-start items-center p-2 h-fit truncate";
  const state = isActive ? "border-2 border-dashed border-primary/30 bg-transparent text-primary hover:bg-primary/5" : "bg-transparent text-primary/80 hover:text-primary hover:bg-primary/5";
  const content = <>
        <span className="mr-2 flex h-4 w-4 flex-shrink-0 items-center justify-center" aria-hidden="true">
          {icon}
        </span>
        <span className="text-sm font-normal flex items-center">
          {children}
          {badge && <sup>
              <span className="ml-1 inline-block h-2 w-2 rounded-full bg-success" />
            </sup>}
        </span>
      </>;
  if (href) {
    return <a href={href}>
          <button className={cn(base, state)} type="button" ref={ref}>
            {content}
          </button>
        </a>;
  }
  return <button className={cn(base, state)} type="button" onClick={onClick} ref={ref}>
        {content}
      </button>;
});
NavButton.displayName = "NavButton";
const RoomItemButton: React.FC<{
  room: Room;
  onDelete: (roomId: string) => Promise<void>;
  onVisibilityChange: (roomId: string, visibility: "public" | "private") => Promise<void>;
  onRename: (roomId: string, newName: string) => Promise<void>;
}> = ({
  room,
  onDelete,
  onVisibilityChange,
  onRename,
}) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(room.name);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  React.useEffect(() => {
    setEditName(room.name);
  }, [room.name]);

  const handleDelete = async () => {
    await onDelete(room.id);
  };

  const handleVisibilityChange = async (visibility: "public" | "private") => {
    await onVisibilityChange(room.id, visibility);
  };

  const handleRenameSubmit = async () => {
    const trimmedName = editName.trim();
    if (trimmedName && trimmedName !== room.name) {
      await onRename(room.id, trimmedName);
    } else {
      setEditName(room.name);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === "Escape") {
      setEditName(room.name);
      setIsEditing(false);
    }
  };

  return (
    <div
      className="relative group/item"
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => setShowMenu(false)}
    >
      {isEditing ? (
        <div className="flex items-center p-2 space-x-2">
          {room.team_id ? <TeamIcon className="h-4 w-4 flex-shrink-0 text-primary/80" size={16} /> : <Box className="h-4 w-4 flex-shrink-0 text-primary/80" />}
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm font-normal text-primary outline-none min-w-0"
          />
        </div>
      ) : (
        <a href={`/room/${room.id}`}>
          <button
            type="button"
            className={cn(
              "inline-flex items-center justify-between rounded-lg bg-transparent p-2 text-left text-primary/80 group-hover/item:bg-primary/5 group-hover/item:text-primary",
              "whitespace-nowrap text-sm font-medium ring-offset-background transition-colors",
              "w-full h-fit space-x-2 truncate"
            )}
          >
            <div className="flex w-full flex-row items-center space-x-2 min-w-0">
              {room.team_id ? <TeamIcon className="h-4 w-4 flex-shrink-0" size={16} /> : <Box className="h-4 w-4 flex-shrink-0" />}
              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                <p className="truncate text-sm font-normal max-w-[130px]">{room.name}</p>
                {room.team_id && <span className="shrink-0 rounded border border-[hsl(var(--selected-border)/0.4)] bg-[hsl(var(--selected-subtle))] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-[hsl(var(--selected))]">Team</span>}
              </div>
            </div>
          </button>
        </a>
      )}

      {!isEditing && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity",
                showMenu ? "opacity-100" : "opacity-100 xl:opacity-0"
              )}
            >
              <MoreVertical className="h-3.5 w-3.5 text-primary" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={() => setShareOpen(true)}>
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <RoomDeleteDialog room={room} onDelete={handleDelete}>
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </RoomDeleteDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <RoomShareDialog
        room={room}
        open={shareOpen}
        onOpenChange={setShareOpen}
        onVisibilityChange={handleVisibilityChange}
      />
    </div>
  );
};
const RecentItemButton: React.FC<{
  item: RecentItem;
  rooms: Room[];
  onAddToRoom: (contentId: string, roomId: string) => Promise<void>;
  onDelete: (contentId: string) => Promise<void>;
  onRename: (contentId: string, newTitle: string) => Promise<void>;
  onVisibilityChange: (contentId: string, visibility: "public" | "private") => void;
}> = ({
  item,
  rooms,
  onAddToRoom,
  onDelete,
  onRename,
  onVisibilityChange,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [shareOpen, setShareOpen] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  React.useEffect(() => {
    setEditTitle(item.title);
  }, [item.title]);

  const handleRenameSubmit = async () => {
    const trimmedTitle = editTitle.trim();
    if (trimmedTitle && trimmedTitle !== item.title) {
      await onRename(item.id, trimmedTitle);
    } else {
      setEditTitle(item.title);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === "Escape") {
      setEditTitle(item.title);
      setIsEditing(false);
    }
  };

  const contentItem: ContentItem = {
    id: item.id,
    title: item.title,
    type: item.type,
    visibility: "private",
    createdAt: "",
  };

  return (
    <div
      className="relative group/item"
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => setShowMenu(false)}
    >
      {isEditing ? (
        <div className="flex items-center p-2 space-x-2">
          <ContentIcon type={item.type} className="h-4 w-4 flex-shrink-0 text-primary/80" />
          <input
            ref={inputRef}
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm font-normal text-primary outline-none min-w-0"
          />
        </div>
      ) : (
        <a href={item.href}>
          <button
            type="button"
            className={cn(
              "inline-flex items-center justify-between rounded-lg bg-transparent p-2 text-left text-primary/80 group-hover/item:bg-primary/5 group-hover/item:text-primary",
              "whitespace-nowrap text-sm font-medium ring-offset-background transition-colors",
              "w-full h-fit space-x-2 truncate"
            )}
          >
            <div className="flex w-full flex-row items-center space-x-2 min-w-0">
              <ContentIcon type={item.type} className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-normal max-w-[160px]">{item.title}</p>
              </div>
            </div>
          </button>
        </a>
      )}

      {!isEditing && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity",
                showMenu ? "opacity-100" : "opacity-100 xl:opacity-0"
              )}
            >
              <MoreVertical className="h-3.5 w-3.5 text-primary" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FolderPlus className="h-4 w-4 mr-2" />
                Add to Room
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="max-h-48 overflow-y-auto">
                  {rooms.length === 0 ? (
                    <DropdownMenuItem disabled>
                      No rooms available
                    </DropdownMenuItem>
                  ) : (
                    rooms.map((room) => (
                      <DropdownMenuItem
                        key={room.id}
                        onClick={() => onAddToRoom(item.id, room.id)}
                      >
                        {room.team_id ? <TeamIcon className="h-4 w-4 mr-2" size={16} /> : <Box className="h-4 w-4 mr-2" />}
                        <span className="truncate max-w-[120px]">{room.name}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={() => setShareOpen(true)}>
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <ContentDeleteDialog title={item.title} onDelete={() => onDelete(item.id)}>
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </ContentDeleteDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ContentShareDialog
        content={contentItem}
        open={shareOpen}
        onOpenChange={setShareOpen}
        onVisibilityChange={onVisibilityChange}
      />
    </div>
  );
};
export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose
}) => {
  const [showMoreRecents, setShowMoreRecents] = useState(false);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [teamRooms, setTeamRooms] = useState<Room[]>([]);
  const [userTeamId, setUserTeamId] = useState<string | null>(null);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [quickGuideOpen, setQuickGuideOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("free");
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [searchContent, setSearchContent] = useState<Array<{
    id: string;
    title: string;
    type: ContentType;
  }>>([]);
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const {
    theme
  } = useTheme();
  const { t } = useTranslation();
  const logo = theme === "light" ? logoLight : logoDark;
  useEffect(() => {
    const fetchRooms = async () => {
      if (!user) {
        setLoadingRooms(false);
        return;
      }
      const {
        data
      } = await supabase.from("rooms").select("*").eq("user_id", user.id).is("team_id", null).order("created_at", {
        ascending: false
      }).limit(10);
      if (data) {
        setRooms(data as Room[]);
      }

      // Fetch team rooms
      const { data: membership } = await supabase
        .from("team_members")
        .select("team_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (membership?.team_id) {
        setUserTeamId(membership.team_id);
        const { data: tRooms } = await supabase
          .from("rooms")
          .select("*")
          .eq("team_id", membership.team_id)
          .order("created_at", { ascending: false })
          .limit(10);
        if (tRooms) setTeamRooms(tRooms as Room[]);
      } else {
        setUserTeamId(null);
        setTeamRooms([]);
      }
      setLoadingRooms(false);
    };
    const fetchRecents = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      const {
        data
      } = await supabase.from("content").select("id, title, type, created_at").order("created_at", {
        ascending: false
      }).limit(10);
      if (data) {
        const mapped: RecentItem[] = data.map(item => ({
          id: item.id,
          title: item.title,
          type: item.type as ContentType,
          href: item.type === "chat" ? `/chat/${item.id}` : `/content/${item.id}`
        }));
        setRecentItems(mapped);
      }
      setLoading(false);
    };
    const fetchSearchContent = async () => {
      if (!user) return;
      const {
        data
      } = await supabase.from("content").select("id, title, type").order("created_at", {
        ascending: false
      });
      if (data) {
        setSearchContent(data.map(item => ({
          id: item.id,
          title: item.title,
          type: item.type as ContentType
        })));
      }
    };
    const fetchSubscriptionStatus = async () => {
      if (!user) return;
      const {
        data
      } = await supabase.from("profiles").select("subscription_status, subscription_plan").eq("id", user.id).single();
      if (data) {
        setSubscriptionStatus(data.subscription_status || "free");
        setSubscriptionPlan(data.subscription_plan);
      }
    };
    fetchRooms();
    fetchRecents();
    fetchSearchContent();
    fetchSubscriptionStatus();

    // Set up realtime subscription for profile changes
    const profileChannel = supabase.channel("profile-changes").on("postgres_changes", {
      event: "UPDATE",
      schema: "public",
      table: "profiles",
      filter: `id=eq.${user?.id}`
    }, payload => {
      if (payload.new.subscription_status !== undefined) {
        setSubscriptionStatus(payload.new.subscription_status || "free");
      }
      if (payload.new.subscription_plan !== undefined) {
        setSubscriptionPlan(payload.new.subscription_plan);
      }
    }).subscribe();

    // Set up realtime subscription for rooms
    const roomsChannel = supabase.channel("rooms-changes").on("postgres_changes", {
      event: "INSERT",
      schema: "public",
      table: "rooms",
      filter: `user_id=eq.${user?.id}`
    }, payload => {
      const newRoom = payload.new as Room;
      setRooms(prev => [newRoom, ...prev.slice(0, 9)]);
    }).subscribe();

    // Set up realtime subscription for content
    const contentChannel = supabase.channel("content-changes").on("postgres_changes", {
      event: "INSERT",
      schema: "public",
      table: "content",
      filter: `user_id=eq.${user?.id}`
    }, payload => {
      const newItem: RecentItem = {
        id: payload.new.id,
        title: payload.new.title,
        type: payload.new.type as ContentType,
        href: payload.new.type === "chat" ? `/chat/${payload.new.id}` : `/content/${payload.new.id}`
      };
      setRecentItems(prev => [newItem, ...prev.slice(0, 9)]);
    }).subscribe();

    // Listen for content uploads to refresh the list (fallback)
    const handleContentUploaded = () => {
      fetchRecents();
    };
    window.addEventListener("content-uploaded", handleContentUploaded);
    return () => {
      supabase.removeChannel(profileChannel);
      supabase.removeChannel(roomsChannel);
      supabase.removeChannel(contentChannel);
      window.removeEventListener("content-uploaded", handleContentUploaded);
    };
  }, [user]);
  const getUserName = () => {
    if (!user) return "Guest";
    return user.user_metadata?.full_name || user.email?.split("@")[0] || "User";
  };
  const getUserInitials = () => {
    if (!user) return "G";
    const name = user.user_metadata?.full_name;
    if (name) {
      const parts = name.split(" ");
      return parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase();
    }
    return (user.email?.[0] || "U").toUpperCase();
  };
  const handleCreateRoom = async (teamId?: string) => {
    if (!user) return;
    try {
      const insertData: { user_id: string; team_id?: string } = { user_id: user.id };
      if (teamId) insertData.team_id = teamId;
      const { data, error } = await supabase.from("rooms").insert([insertData]).select().single();
      if (error) throw error;
      if (data) {
        if (teamId) {
          setTeamRooms(prev => [data as Room, ...prev]);
        } else {
          setRooms(prev => [data as Room, ...prev]);
        }
        
        toast({
          title: teamId ? "Team room created" : "Room created",
          description: teamId ? "Your new team room has been created successfully." : "Your new room has been created successfully."
        });
        navigate(`/room/${data.id}`);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create room. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteRoom = async (roomId: string) => {
    try {
      const { error } = await supabase.from("rooms").delete().eq("id", roomId);
      if (error) throw error;
      setRooms(prev => prev.filter(r => r.id !== roomId));
      toast({
        title: "Room deleted",
        description: "The room has been deleted successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete room. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleVisibilityChange = async (roomId: string, visibility: "public" | "private") => {
    try {
      const { error } = await supabase
        .from("rooms")
        .update({ visibility })
        .eq("id", roomId);
      if (error) throw error;
      setRooms(prev => prev.map(r => r.id === roomId ? { ...r, visibility } : r));
      toast({
        title: "Visibility updated",
        description: `Room is now ${visibility}.`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update visibility. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleRenameRoom = async (roomId: string, newName: string) => {
    try {
      const { error } = await supabase
        .from("rooms")
        .update({ name: newName })
        .eq("id", roomId);
      if (error) throw error;
      setRooms(prev => prev.map(r => r.id === roomId ? { ...r, name: newName } : r));
      toast({
        title: "Room renamed",
        description: "Room name has been updated."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to rename room. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAddContentToRoom = async (contentId: string, roomId: string) => {
    try {
      const { error } = await supabase
        .from("content")
        .update({ room_id: roomId })
        .eq("id", contentId);
      if (error) throw error;
      const room = rooms.find(r => r.id === roomId);
      toast({
        title: "Added to room",
        description: `Content added to "${room?.name || 'room'}".`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add content to room. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    try {
      const { error } = await supabase
        .from("content")
        .delete()
        .eq("id", contentId);
      if (error) throw error;
      setRecentItems(prev => prev.filter(item => item.id !== contentId));
      toast({
        title: "Content deleted",
        description: "The content has been deleted successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete content. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleRenameContent = async (contentId: string, newTitle: string) => {
    try {
      const { error } = await supabase
        .from("content")
        .update({ title: newTitle })
        .eq("id", contentId);
      if (error) throw error;
      setRecentItems(prev => prev.map(item => item.id === contentId ? { ...item, title: newTitle } : item));
      toast({
        title: "Content renamed",
        description: "Content title has been updated."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to rename content. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleContentVisibilityChange = (contentId: string, visibility: "public" | "private") => {
    // Update local state if needed
    setRecentItems(prev => prev.map(item => item.id === contentId ? { ...item } : item));
  };

  const visibleRecents = showMoreRecents ? recentItems : recentItems.slice(0, RECENTS_LIMIT);
  return <>
      {isOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />}

      <aside className={cn("fixed inset-y-0 left-0 z-50 w-64 border-r border-border bg-secondary text-foreground", "lg:bg-secondary/50 lg:backdrop-blur-sm", "transition-transform duration-200 ease-in-out", isOpen ? "translate-x-0" : "-translate-x-full")}>
        <div className="flex h-full flex-col px-1 py-4">
          <div className="mb-6 ml-4 mr-2 flex items-center justify-between">
            <a href="/dashboard" className="w-fit">
              <div className="flex items-center gap-2">
                <img src={logo} className="h-auto w-40 max-w-full cursor-pointer" alt="Shattara AI" />
              </div>
            </a>

            <button type="button" onClick={onClose} aria-label="Collapse sidebar" className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted transition">
              <ChevronsLeft className="h-5 w-5" />
            </button>
          </div>

          <div className="text-sm font-medium">
            <div className="px-2 pb-2 space-y-1">
              <NavButton icon={<Plus className="h-4 w-4" />} href="/dashboard" isActive>
                {t("navigation.newChat")}
              </NavButton>
              <NavButton icon={<Search className="h-4 w-4" />} onClick={() => setSearchOpen(true)}>
                {t("navigation.search")}
              </NavButton>
              <NavButton icon={<History className="h-4 w-4" />} href="/history">
                {t("navigation.history")}
              </NavButton>
              <NavButton icon={<ChartNoAxesColumnIncreasing className="h-4 w-4" />} href="/reports">
                {t("navigation.reports")}
              </NavButton>
            </div>
          </div>

          <ScrollArea className="mt-4 flex-grow">
            <nav className="h-full w-full">
              <ul className="flex h-full min-h-[80vh] flex-col items-start space-y-6 px-2 text-sm">
                <li className="w-full">
                  <p className="mb-2 ml-2 text-xs font-bold uppercase tracking-wide text-muted-foreground/80">
                    {t("navigation.rooms")}
                  </p>
                  <div className="flex flex-col space-y-1">
                    {userTeamId ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" className={cn("inline-flex items-center space-x-2 rounded-lg border-2 border-dashed border-primary/30 bg-transparent p-2 text-left text-primary hover:bg-primary/5", "whitespace-nowrap text-sm font-medium ring-offset-background transition-colors", "w-full h-fit")}>
                            <Plus className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm font-normal">{t("dashboard.createRoom")}</span>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          <DropdownMenuItem onClick={() => handleCreateRoom()}>
                            <Box className="h-4 w-4 mr-2" />
                            Personal Room
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCreateRoom(userTeamId)}>
                            <TeamIcon className="h-4 w-4 mr-2" size={16} />
                            Team Room
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <button type="button" onClick={() => handleCreateRoom()} className={cn("inline-flex items-center space-x-2 rounded-lg border-2 border-dashed border-primary/30 bg-transparent p-2 text-left text-primary hover:bg-primary/5", "whitespace-nowrap text-sm font-medium ring-offset-background transition-colors", "w-full h-fit")}>
                        <Plus className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm font-normal">{t("dashboard.createRoom")}</span>
                      </button>
                    )}

                    {loadingRooms ? <p className="px-2 py-4 text-xs text-muted-foreground">{t("common.loading")}</p> : [...rooms, ...teamRooms].length === 0 ? <p className="px-2 py-4 text-xs text-muted-foreground">{t("dashboard.noRooms")}</p> : <>
                        {rooms.map(room => <RoomItemButton key={room.id} room={room} onDelete={handleDeleteRoom} onVisibilityChange={handleVisibilityChange} onRename={handleRenameRoom} />)}
                        {teamRooms.map(room => <RoomItemButton key={room.id} room={room} onDelete={handleDeleteRoom} onVisibilityChange={handleVisibilityChange} onRename={handleRenameRoom} />)}
                      </>}
                  </div>
                </li>

                <li className="w-full">
                  <p className="mb-2 ml-2 text-xs font-bold uppercase tracking-wide text-muted-foreground/80">
                    {t("navigation.recents")}
                  </p>
                  <div className="flex flex-col space-y-1">
                    {loading ? <p className="px-2 py-4 text-xs text-muted-foreground">{t("common.loading")}</p> : recentItems.length === 0 ? <p className="px-2 py-4 text-xs text-muted-foreground">
                        {t("dashboard.noRecents")}
                      </p> : <>
                        {visibleRecents.map(item => <RecentItemButton key={item.id} item={item} rooms={[...rooms, ...teamRooms]} onAddToRoom={handleAddContentToRoom} onDelete={handleDeleteContent} onRename={handleRenameContent} onVisibilityChange={handleContentVisibilityChange} />)}

                        {recentItems.length > RECENTS_LIMIT && <button type="button" onClick={() => setShowMoreRecents(prev => !prev)} className={cn("inline-flex items-center space-x-2 rounded-lg bg-transparent p-2 text-left text-primary/80 hover:bg-primary/5 hover:text-primary", "whitespace-nowrap text-sm font-medium ring-offset-background transition-colors", "w-full h-fit truncate")}>
                            <ChevronRight className={cn("mr-1 h-4 w-4 transition-transform", showMoreRecents ? "rotate-90" : "")} />
                            <span className="truncate text-sm font-normal">
                              {showMoreRecents ? t("navigation.seeLess") : t("navigation.seeAll")}
                            </span>
                          </button>}
                      </>}
                  </div>
                </li>

                <li className="w-full">
                  <p className="mb-2 ml-2 text-xs font-bold uppercase tracking-wide text-muted-foreground/80">
                    {t("navigation.helpAndTools")}
                  </p>
                  <div className="flex flex-col space-y-1">
                    <FeedbackPopover>
                      <NavButton icon={<ThumbsUp className="h-4 w-4" />}>{t("navigation.feedback")}</NavButton>
                    </FeedbackPopover>
                    <NavButton icon={<Book className="h-4 w-4" />} badge onClick={() => setQuickGuideOpen(true)}>
                      {t("navigation.quickGuide")}
                    </NavButton>
                    <a href="https://discord.gg/uD8cc9mEh4" target="_blank" rel="noopener noreferrer">
                      <NavButton icon={<DiscordIcon className="h-4 w-4" />}>
                        {t("navigation.joinCommunity")}
                      </NavButton>
                    </a>
                     {/* Survey button hidden temporarily */}
                     {/* <NavButton icon={<NotebookPen className="h-4 w-4" />}>{t("navigation.survey")}</NavButton> */}
                    
                    <NavButton icon={<Calculator className="h-4 w-4" />} onClick={() => setCalculatorOpen(true)}>
                      {t("navigation.gpaCalculator")}
                    </NavButton>
                  </div>
                </li>
              </ul>
            </nav>
          </ScrollArea>

          <div className="mt-2 flex w-full flex-col">
            <div className="flex flex-col items-center justify-center">
              <div className="flex min-w-[12.5rem] items-center justify-center rounded-tl-xl rounded-tr-xl border-t-2 border-l-2 border-r-2 border-success/50 bg-success/10 px-6 py-1 text-xs text-success backdrop-blur-md">
                <p>{subscriptionStatus === "active" ? (subscriptionPlan === "team" ? t("common.teamPlan") || "Team Plan" : t("common.proPlan")) : t("common.freePlan")}</p>
              </div>

              <div className="w-full text-sm font-medium">
                <UserProfileDropdown userName={getUserName()} userInitials={getUserInitials()} />
              </div>
            </div>
          </div>
        </div>
      </aside>

      <SearchCommand open={searchOpen} onOpenChange={setSearchOpen} content={searchContent} rooms={[...rooms, ...teamRooms]} />
      <CalculatorDialog open={calculatorOpen} onOpenChange={setCalculatorOpen} />
      <QuickGuideDialog open={quickGuideOpen} onOpenChange={setQuickGuideOpen} />
    </>;
};